import { createApp } from 'vue';
import { createRouter, createWebHistory, type RouteLocationNormalizedLoaded, type Router } from 'vue-router';

// import VueChatScroll from 'vue-chat-scroll'
import ElementPlus from 'element-plus';
import VueGoogleMaps from '@fawmi/vue-google-maps';
import VueChatScroll from 'vue-chat-scroll';
import VueGtag from 'vue-gtag';
import queryString from 'query-string';
import { createGtm } from '@gtm-support/vue-gtm';

// editor
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';

import registerGlobalElements from './globalComponents';
import SideBar from './components/UIComponents/SidebarPlugin';
import RightPanel from './components/UIComponents/RightPanelPlugin';

import App from './App.vue';

// router setup
import routes from './routes/routes';

import util from '@/Utils/util';
import { availableRolesForPage } from '@/constants/app';

import i18n from '@/plugins/i18n';
import VueDOMPurifyHTML from 'vue-dompurify-html';

import 'element-plus/dist/index.css';
import './assets/sass/paper-dashboard.scss';
import './assets/sass/element_variables.scss';
import './assets/sass/demo.scss';
import { log } from './Utils/logger';
import { loadLogLevelAndInitializeApm } from '@/Utils/apmInit';

import { ALLOWED_ATTR, ALLOWED_TAGS } from '@/constants/app';
import { startExtensionAuthSync } from '@/services/extensionAuth';

loadLogLevelAndInitializeApm();
startExtensionAuthSync();

log.init();
log.setLevel(String(localStorage.getItem('logLevel')));

declare module '@vue/runtime-core' {
  interface ComponentCustomProperties {
    $sidebar: any;
    $rightpanel: any;
    $notify: any;
    $route: RouteLocationNormalizedLoaded;
    $router: Router;
    $loading: any;
    $alert: any;
    $prompt: any;
    $gmapApiPromiseLazy: any;
    $ga: any;
  }
}

const app = createApp(App);

app.use(ElementPlus);
app.use(VueChatScroll);
// global quilljs editor registration
app.component('QuillEditor', QuillEditor);
// Google maps configuration
app.use(VueGoogleMaps, {
  load: {
    key: util.googleApiKey,
    libraries: 'places', // necessary for places input
    v: 'quarterly',
  },
});
app.use(VueDOMPurifyHTML, {
  default: {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
  },
});
// app.use(VueChatScroll)

registerGlobalElements(app);

app.use(SideBar, {});
app.use(RightPanel);

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

const getNewPathWithOperatorId = (to: any) => {
  if (!to.path.startsWith('/operator')) {
    return;
  }

  if (to.query?.operatorId) {
    return;
  }

  const operatorId = localStorage.getItem('operatorId');
  if (!operatorId) {
    return;
  }

  const query = to.query;
  query.operatorId = operatorId;

  const queryParams = queryString.stringify(query) as string;
  let newPath = `${to.path}?${queryParams}`;

  if (to.hash) {
    newPath += to.hash;
  }

  return newPath;
};

router.beforeEach((to: any, from: any, next: any) => {
  log.info(
    {
      func: 'router.beforeEach',
      to: to?.href,
      from: from?.href,
    },
    'navigation',
  );

  const requiresAuth = to.matched.some((record: any) => record.meta.requiresAuth);
  const hasPagePermission = to.matched.some((record: any) => record.meta.roles);
  const userRoles = JSON.parse(localStorage.getItem('roles') || '[]');
  const requiresTwoFactor = localStorage.getItem('requiresTwoFactor') === 'true';

  if (to.path === '/register' || to.path === '/registration' || to.path === '/signup' || to.path === '/join') {
    location.href = 'https://gogograndparent.com/register';
    return;
  }

  if (localStorage.getItem('access_token') && userRoles.length === 0) {
    localStorage.clear();
    next('/login');
    return;
  }

  // Handle two-factor authentication
  if (requiresTwoFactor && to.path !== '/otp-login') {
    next('/otp-login');
    return;
  }

  if (requiresAuth) {
    if (!localStorage.getItem('access_token')) {
      next('/login');
      return;
    }
  }

  let isPermissionAllowed = true;
  if (hasPagePermission) {
    for (const route of to.matched) {
      let isRouteAvailable = true;
      if (route.meta.roles) {
        isRouteAvailable = route.meta.roles.some((routeRole: string) => userRoles.includes(routeRole));
      }

      isPermissionAllowed = isPermissionAllowed && isRouteAvailable;
    }
  }

  const newPath = getNewPathWithOperatorId(to);

  if (isPermissionAllowed) {
    newPath ? next(newPath) : next();
  } else {
    let pageCategory = '';
    for (const page of availableRolesForPage) {
      const isPageIncludeUserRole = page.allowedRoles.some((role: string) => userRoles.includes(role));

      if (isPageIncludeUserRole) {
        pageCategory = page.pageId;
        break;
      }
    }

    next('/' + pageCategory);
  }
});

app.use(
  VueGtag,
  {
    includes: util.isProdMode
      ? [
          {
            id: 'UA-76449918-1',
          },
          {
            id: 'G-62RJKKDKT9',
          },
        ]
      : [],
    config: {
      // TODO [kzurbano] the non-prod id here is my own personal setup
      // We can replace it with a different id once we have
      // google analytics property created specifically for development
      id: util.isProdMode ? 'UA-125673581-1' : 'UA-212743370-1',
    },
  },
  router,
);

app.use(
  createGtm({
    id: 'GTM-MXG52BCK',
    vueRouter: router,
    enabled: true,
  }),
);

app.use(router).use(i18n).mount('#app');
